import { useState } from 'react';

import { DataTable, DataTableCell, DataTableHead, DataTableRow } from '@/components/table/DataTable';
import { TablePager } from '@/components/table/TablePager';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { type AiAlias, aiApi } from '@/lib/aiAdmin';
import { formatCount } from '@/lib/format';

import { CENTER, TITLE, columns, confirmedWrite, LoadFailed, Loading, useCursorList } from './common';

/** The names callers use ("standard") and the model each one points to, with the largest answer allowed. */
export function AliasesTab({ modelIds }: { modelIds: string[] }) {
  const list = useCursorList(async (token) => {
    const page = await aiApi.aliases(token);
    return { rows: page.aliases ?? [], next: page.nextPageToken };
  }, []);
  const [editing, setEditing] = useState<string | undefined>();
  const [model, setModel] = useState('');
  const [ceiling, setCeiling] = useState('');
  const [saving, setSaving] = useState(false);

  const edit = (alias: AiAlias) => {
    setEditing(alias.name);
    setModel(alias.modelId);
    setCeiling(String(alias.maxTokensCeiling ?? ''));
  };

  const save = async (alias: AiAlias) => {
    const value = Number(ceiling);
    if (!Number.isInteger(value) || value < 1) return void window.alert('The ceiling must be a whole number above 0.');
    setSaving(true);
    const ok = await confirmedWrite(
      `Change ${alias.name} to ${model} with a ceiling of ${formatCount(value)} tokens? It applies to every organization within about 30 seconds.`,
      () => aiApi.putAlias({ name: alias.name, modelId: model, maxTokensCeiling: value }),
      `${alias.name} changed`,
      'The change failed',
    );
    setSaving(false);
    if (ok) {
      setEditing(undefined);
      list.reload();
    }
  };

  if (list.error) return <LoadFailed message={list.error} onRetry={list.reload} />;
  const models = [...new Set([...modelIds, model].filter(Boolean))];
  return (
    <DataTable footer={<TablePager {...list.pager} />}>
      <DataTableHead columns={columns(['Alias', 'Model', 'Ceiling (tokens)', 'Description', ''])} />
      <tbody>
        {list.rows.map((alias) => {
          const open = editing === alias.name;
          return (
            <DataTableRow key={alias.name}>
              <DataTableCell className={TITLE}>{alias.name}</DataTableCell>
              <DataTableCell className={CENTER}>
                {open ? (
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger size="sm" className="mx-auto w-52" aria-label={`Model of ${alias.name}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {models.map((id) => (
                        <SelectItem key={id} value={id}>
                          {id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  alias.modelId
                )}
              </DataTableCell>
              <DataTableCell className={`${CENTER} tabular-nums`}>
                {open ? (
                  <Input type="number" min={1} value={ceiling} onChange={(event) => setCeiling(event.target.value)} aria-label={`Ceiling of ${alias.name}`} className="mx-auto h-8 w-28 text-center" />
                ) : alias.maxTokensCeiling ? (
                  formatCount(alias.maxTokensCeiling)
                ) : (
                  'none'
                )}
              </DataTableCell>
              <DataTableCell className={`text-center max-w-[20rem] text-muted-foreground`}>{alias.description || '—'}</DataTableCell>
              <DataTableCell className={CENTER}>
                {open ? (
                  <div className="inline-flex gap-2">
                    <Button type="button" size="sm" disabled={saving} onClick={() => void save(alias)}>
                      Save
                    </Button>
                    <Button type="button" size="sm" variant="outline" disabled={saving} onClick={() => setEditing(undefined)}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button type="button" size="sm" variant="outline" disabled={editing !== undefined} onClick={() => edit(alias)}>
                    Edit
                  </Button>
                )}
              </DataTableCell>
            </DataTableRow>
          );
        })}
        {list.loading && list.rows.length === 0 ? (
          <tr>
            <td colSpan={5}>
              <Loading label="Loading aliases" />
            </td>
          </tr>
        ) : null}
        {!list.loading && list.rows.length === 0 ? (
          <tr>
            <td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">
              ai has no aliases.
            </td>
          </tr>
        ) : null}
      </tbody>
    </DataTable>
  );
}
